/**
 * Order history, documents and archive marks (migration 20261029110000).
 *  - Dataset `order_activity` (scope = order ID): status changes, archive marks and,
 *    with documents.read, added documents. Actor names only for workspace members.
 *  - Dataset `order_documents` (scope = order ID): active files, latest version only,
 *    listed only when the caller may download them (same rule as the Storage policy).
 *  - app.set_orders_archived: reversible local archive, all-or-nothing per call.
 */
import type { Transport } from './transport.ts';
import type { Filter, Sort } from './query.ts';

export type ActivityEvent = 'order_created' | 'status_changed' | 'archived' | 'restored' | 'document_added'
  | 'send_accepted' | 'send_failed' | 'send_unknown' | 'send_pending' | 'send_cancelled';

export interface ActivityRaw {
  id: string;
  occurred_at: string;
  event: ActivityEvent;
  /** status_changed/order_created: the status; document_added: the document kind. */
  detail: string | null;
  /** Sends: the first recipient. */
  label: string | null;
  /** Null for system events and for actors who are no longer members. */
  actor_name: string | null;
  actor_kind: 'member' | 'system';
}

export type DocumentKind = 'pickup_photo' | 'delivery_photo' | 'bol' | 'check' | 'invoice' | 'other';

export interface DocumentRaw {
  /** Assignment ID (a file assigned to two vehicles appears twice). */
  id: string;
  order_id: string;
  document_id: string;
  /** The exact version listed (latest at read time). */
  version_id: string;
  kind: DocumentKind;
  /** Null: the file belongs to the whole order. */
  vehicle_id: string | null;
  version_number: number;
  uploaded_at: string;
  media_type: string;
  byte_count: number;
  object_path: string;
}

export const DOCUMENTS_BUCKET = 'miraops-private-documents';

/** Server cap per archive call. */
export const ARCHIVE_BATCH = 500;

export interface ArchiveResult { orderId: string; archivedAt: string | null; revision: number; changed: boolean }

/**
 * Archives or restores orders in server-sized batches. Each batch is all-or-nothing;
 * a failure stops before later batches and reports how many were already applied.
 */
export async function setOrdersArchived(transport: Transport, workspaceId: string, orderIds: readonly string[], archive: boolean,
  signal?: AbortSignal): Promise<ArchiveResult[]> {
  const out: ArchiveResult[] = [];
  const unique = [...new Set(orderIds)];
  for (let i = 0; i < unique.length; i += ARCHIVE_BATCH) {
    const rows = await transport.rpc<{ order_id: string; archived_at: string | null; revision: number; changed: boolean }[]>(
      'set_orders_archived', { w: workspaceId, order_ids: unique.slice(i, i + ARCHIVE_BATCH), archive }, signal);
    out.push(...rows.map(r => ({ orderId: r.order_id, archivedAt: r.archived_at, revision: Number(r.revision), changed: r.changed })));
  }
  return out;
}

export interface CollectOptions { max: number; signal?: AbortSignal; onProgress?: (loaded: number) => void }
export interface Collected<Raw> { rows: Raw[]; truncated: boolean }

/**
 * Reads every row of a query, in server order, up to `max`. `truncated` means more
 * rows matched than were read; callers must say so rather than imply completeness.
 */
export async function collectRows<Raw>(transport: Transport, workspaceId: string,
  spec: { dataset: string; scope?: string | null; filters: Filter[]; sorts: Sort[] }, options: CollectOptions): Promise<Collected<Raw>> {
  const rows: Raw[] = [];
  let cursor: string | null = null;
  for (;;) {
    const page: { rows: Raw[]; next_cursor: string | null } = await transport.rpc('query_shared_table', {
      w: workspaceId, dataset: spec.dataset, scope: spec.scope ?? null, filters: spec.filters, sorts: spec.sorts, page_size: 100, cursor,
    }, options.signal);
    for (const row of page.rows) {
      if (rows.length === options.max) return { rows, truncated: true };
      rows.push(row);
    }
    options.onProgress?.(rows.length);
    cursor = page.next_cursor;
    if (!cursor) return { rows, truncated: false };
  }
}

/** Removing a file attached by mistake (migration 20261103020000): archived, not erased. */
export type RemoveDocumentOutcome = 'changed' | 'unchanged';
const DOCUMENT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * Removes a file from every order it is on: it stops being listed, opened or shared, and the
 * removal is recorded. Refused for an invoice's own file (`invoice_file`). Needs documents.write.
 */
export async function removeDocument(api: Transport, w: string, documentId: string, reason?: string, signal?: AbortSignal): Promise<RemoveDocumentOutcome> {
  const why = reason?.trim() || null;
  if (!DOCUMENT_ID_RE.test(documentId) || (why !== null && why.length > 200)) throw Error('invalid_request');
  return api.rpc<RemoveDocumentOutcome>('remove_document', { w, doc: documentId, reason: why }, signal);
}

/** Replacing a file (migration 20261103030000): the corrected file is uploaded first (uploadDocument). */
export type ReplaceDocumentOutcome = 'changed' | 'unchanged';
/**
 * Puts the uploaded corrected file (newDocumentId) everywhere the old one was and archives the old
 * one; recorded. Refused for another kind (`kind_mismatch`), a file already replaced
 * (`already_replaced`) or an invoice's own file (`invoice_file`). Needs documents.write.
 */
export async function replaceDocument(api: Transport, w: string, oldDocumentId: string, newDocumentId: string, reason?: string, signal?: AbortSignal): Promise<ReplaceDocumentOutcome> {
  const why = reason?.trim() || null;
  if (!DOCUMENT_ID_RE.test(oldDocumentId) || !DOCUMENT_ID_RE.test(newDocumentId) || oldDocumentId === newDocumentId || (why !== null && why.length > 200)) throw Error('invalid_request');
  return api.rpc<ReplaceDocumentOutcome>('replace_document', { w, old_doc: oldDocumentId, new_doc: newDocumentId, reason: why }, signal);
}
